import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";

import { Bold, Italic, UnderlineIcon, List, ListOrdered } from "lucide-react";

interface RichTextProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled = false,
}: RichTextProps) {
  const editor = useEditor({
    content: value || "",
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
      }),
      Underline,
      Placeholder.configure({
        placeholder: placeholder || "",
      }),
    ],
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  // El editor solo lee `value` al nacer; si el valor cambia desde fuera (se
  // elige un puesto y se autollena, se cancela una edición) la pantalla se
  // quedaba con lo anterior aunque el estado ya dijera otra cosa. Lo que
  // escribe la propia persona no entra aquí: ya coincide con el contenido.
  useEffect(() => {
    if (!editor) return;
    const vacio = (html: string) => html === "" || html === "<p></p>";
    const actual = editor.getHTML();
    const nuevo = value || "";
    if (nuevo === actual || (vacio(nuevo) && vacio(actual))) return;
    editor.commands.setContent(nuevo, { emitUpdate: false });
  }, [editor, value]);

  if (!editor) return null;

  const button =
    "p-2 rounded-md hover:bg-gray-200 active:bg-gray-300 transition text-gray-700";
  const active =
    "p-2 rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 active:bg-blue-300";

  return (
    <div className="border border-gray-300 rounded-lg p-2">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 border-b border-gray-300 pb-2 mb-2">
        {/* Bold */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
          className={editor.isActive("bold") ? active : button}
        >
          <Bold size={16} />
        </button>

        {/* Italic */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={disabled}
          className={editor.isActive("italic") ? active : button}
        >
          <Italic size={16} />
        </button>

        {/* Underline */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={disabled}
          className={editor.isActive("underline") ? active : button}
        >
          <UnderlineIcon size={16} />
        </button>

        {/* Divider */}
        <div className="mx-2 w-px bg-gray-300" />

        {/* Bullet list */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={disabled}
          className={editor.isActive("bulletList") ? active : button}
        >
          <List size={16} />
        </button>

        {/* Ordered list */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={disabled}
          className={editor.isActive("orderedList") ? active : button}
        >
          <ListOrdered size={16} />
        </button>
      </div>

      {/* Editor area */}
      <div
        className={`tiptap-editor min-h-[150px] px-2 py-1 ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-text"}`}
        onClick={() => {
          if (!disabled) editor.commands.focus();
        }}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
